import React, { useState, useEffect } from "react";
import { proxyApi } from "../../../../../services/api/multi/proxies";
import { webshareApi } from "../../../../../services/api/multi/webshare_credentials";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  Add as AddIcon,
  CloudDownload as CloudDownloadIcon,
} from "@mui/icons-material";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

const ProxySubSubTab = () => {
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [webshareLoading, setWebshareLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProxy, setEditingProxy] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testingProxyId, setTestingProxyId] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [hasActiveWebshareCredential, setHasActiveWebshareCredential] =
    useState(false);

  // Form state
  const [formData, setFormData] = useState({
    host: "",
    port: "",
    username: "",
    password: "",
    active: true,
  });
  const [quickImport, setQuickImport] = useState("");

  useEffect(() => {
    fetchProxies();
    checkWebshareCredentials();

    const handleCredentialChange = () => {
      checkWebshareCredentials(true);
    };

    window.addEventListener(
      "activeWebshareCredentialChanged",
      handleCredentialChange,
    );

    return () => {
      window.removeEventListener(
        "activeWebshareCredentialChanged",
        handleCredentialChange,
      );
    };
  }, []);

  // Clear message after 3 seconds or custom duration
  useEffect(() => {
    if (message.text) {
      const duration = message.duration || 3000;
      const timer = setTimeout(
        () => setMessage({ type: "", text: "" }),
        duration,
      );
      return () => clearTimeout(timer);
    }
  }, [message]);

  const fetchProxies = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const CACHE_KEY = "proxies_cache";
      const cached = localStorage.getItem(CACHE_KEY);
      const CACHE_DURATION = 5 * 60 * 1000;

      if (!forceRefresh && cached && typeof forceRefresh !== "object") {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          setProxies(data);
          setLoading(false);
          return;
        }
      }

      const response = await proxyApi.getProxies();

      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: response.data,
          timestamp: Date.now(),
        }),
      );

      setProxies(response.data);
    } catch (error) {
      console.error("Error fetching proxies:", error);
      setMessage({ type: "error", text: "Failed to fetch proxies" });
    } finally {
      setLoading(false);
    }
  };

  const checkWebshareCredentials = async (forceRefresh = false) => {
    try {
      const CACHE_KEY = "webshare_credentials_check_cache";
      const cached = localStorage.getItem(CACHE_KEY);
      const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

      if (!forceRefresh && cached && typeof forceRefresh !== "object") {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          const hasActive = data.some((cred) => cred.active);
          setHasActiveWebshareCredential(hasActive);
          return;
        }
      }

      const response = await webshareApi.getWebshareCredentials();
      const credentials = response.data || [];

      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: credentials,
          timestamp: Date.now(),
        }),
      );

      const hasActive = credentials.some((cred) => cred.active);
      setHasActiveWebshareCredential(hasActive);
    } catch (error) {
      console.error("Error checking Webshare credentials:", error);
    }
  };

  const handleFetchWebshareProxies = async () => {
    setWebshareLoading(true);
    try {
      await webshareApi.fetchWebshareProxies();
      setMessage({
        type: "success",
        text: "Proxies fetched from Webshare successfully",
      });
      fetchProxies(true);
    } catch (error) {
      console.error("Error fetching Webshare proxies:", error);
      setMessage({ type: "error", text: "Failed to fetch Webshare proxies" });
    } finally {
      setWebshareLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleQuickImportChange = (e) => {
    const value = e.target.value;
    setQuickImport(value);

    if (value) {
      // Format: host:port:username:password
      const parts = value.split(":");
      if (parts.length >= 2) {
        const updates = {
          host: parts[0],
          port: parts[1],
        };
        if (parts.length >= 3) updates.username = parts[2];
        if (parts.length >= 4) updates.password = parts[3];

        setFormData((prev) => ({ ...prev, ...updates }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        port: parseInt(formData.port),
      };

      if (editingProxy) {
        await proxyApi.updateProxy(editingProxy.id, payload);
        setMessage({ type: "success", text: "Proxy updated successfully" });
      } else {
        await proxyApi.createProxy(payload);
        setMessage({ type: "success", text: "Proxy created successfully" });
      }
      closeModal();
      fetchProxies(true);
    } catch (error) {
      console.error("Error saving proxy:", error);
      setMessage({ type: "error", text: "Failed to save proxy" });
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await proxyApi.toggleProxyActiveStatus(id);
      setMessage({ type: "success", text: "Proxy status updated" });
      fetchProxies(true);
    } catch (error) {
      console.error("Error toggling proxy status:", error);
      setMessage({ type: "error", text: "Failed to toggle proxy status" });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this proxy?")) {
      try {
        await proxyApi.deleteProxy(id);
        setMessage({ type: "success", text: "Proxy deleted successfully" });
        fetchProxies(true);
      } catch (error) {
        console.error("Error deleting proxy:", error);
        setMessage({ type: "error", text: "Failed to delete proxy" });
      }
    }
  };

  const handleDeleteAll = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete ALL proxies? This action cannot be undone.",
      )
    ) {
      try {
        await proxyApi.deleteAllProxies();
        setMessage({
          type: "success",
          text: "All proxies deleted successfully",
        });
        fetchProxies(true);
      } catch (error) {
        console.error("Error deleting all proxies:", error);
        setMessage({ type: "error", text: "Failed to delete all proxies" });
      }
    }
  };

  const handleTest = async (proxy) => {
    setTestingProxyId(proxy.id);
    setTestResult(null);
    try {
      const response = await proxyApi.testProxy({
        proxy_id: proxy.id,
        url: "https://api.ipify.org?format=json",
      });
      if (response.data.success) {
        setMessage({
          type: "success",
          text: `Test successful! IP: ${response.data.proxy_ip}`,
          duration: 5000,
        });
      } else {
        setMessage({
          type: "error",
          text: `Test failed: ${response.data.message}`,
          duration: 8000,
        });
      }
      setTestResult({ id: proxy.id, ...response.data });
      // Refresh list to update fail_count/last_used
      fetchProxies(true);
    } catch (error) {
      console.error("Error testing proxy:", error);
      setMessage({ type: "error", text: "Error testing proxy" });
    } finally {
      setTestingProxyId(null);
    }
  };

  const openModal = (proxy = null) => {
    if (proxy) {
      setEditingProxy(proxy);
      setFormData({
        host: proxy.host,
        port: proxy.port,
        username: proxy.username || "",
        password: proxy.password || "",
        active: proxy.active,
      });
      setQuickImport("");
    } else {
      setEditingProxy(null);
      setFormData({
        host: "",
        port: "",
        username: "",
        password: "",
        active: true,
      });
      setQuickImport("");
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProxy(null);
    setTestResult(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          component="h2"
          sx={{ fontWeight: "bold", color: "text.primary" }}
        >
          Proxy Management
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Tooltip
            title={
              !hasActiveWebshareCredential
                ? "Please activate a Webshare credential in the 'Webshare Credentials' tab first."
                : "Fetch proxies from Webshare using the active credential"
            }
          >
            <span>
              <Button
                variant="outlined"
                color="primary"
                startIcon={
                  webshareLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <CloudDownloadIcon />
                  )
                }
                onClick={handleFetchWebshareProxies}
                disabled={webshareLoading || !hasActiveWebshareCredential}
              >
                {webshareLoading ? "Fetching..." : "Fetch Webshare"}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteAll}
            disabled={loading || proxies.length === 0}
          >
            Delete All
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => openModal()}
          >
            Add Proxy
          </Button>
        </Box>
      </Box>

      {/* Proxy List */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : proxies.length === 0 ? (
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ py: 4 }}
            >
              No proxies found. Click "Add Proxy" to create one.
            </Typography>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Host:Port</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Auth</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Last Used</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Fail Count</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {proxies.map((proxy) => (
                    <TableRow key={proxy.id} hover>
                      <TableCell>
                        {proxy.host}:{proxy.port}
                      </TableCell>
                      <TableCell>
                        {proxy.username ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              color: "success.main",
                            }}
                          >
                            <CheckIcon fontSize="small" sx={{ mr: 0.5 }} /> Yes
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={proxy.active ? "Active" : "Inactive"}
                          color={proxy.active ? "success" : "error"}
                          size="small"
                          onClick={() => handleToggleActive(proxy.id)}
                          sx={{ cursor: "pointer" }}
                        />
                      </TableCell>
                      <TableCell>
                        {proxy.last_used
                          ? new Date(proxy.last_used).toLocaleString()
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={
                            proxy.fail_count > 0 ? "error.main" : "text.primary"
                          }
                          fontWeight={proxy.fail_count > 0 ? "bold" : "normal"}
                        >
                          {proxy.fail_count}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                          }}
                        >
                          <Tooltip title="Test Proxy">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleTest(proxy)}
                                disabled={testingProxyId === proxy.id}
                              >
                                {testingProxyId === proxy.id ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <PlayArrowIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => openModal(proxy)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(proxy.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      <Dialog open={isModalOpen} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingProxy ? "Edit Proxy" : "Add New Proxy"}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {!editingProxy && (
              <Box
                sx={{
                  bgcolor: "primary.light",
                  p: 2,
                  borderRadius: 1,
                  mb: 3,
                  bgOpacity: 0.1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="primary.dark"
                  gutterBottom
                >
                  Quick Import (Host:Port:User:Pass)
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={quickImport}
                  onChange={handleQuickImportChange}
                  placeholder="ultra.marsproxies.com:44443:user:pass"
                  helperText="Paste your proxy string here to auto-fill the fields below."
                  sx={{ bgcolor: "background.paper" }}
                />
              </Box>
            )}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
                mb: 2,
              }}
            >
              <TextField
                label="Host"
                name="host"
                required
                fullWidth
                value={formData.host}
                onChange={handleInputChange}
              />
              <TextField
                label="Port"
                name="port"
                type="number"
                required
                fullWidth
                value={formData.port}
                onChange={handleInputChange}
              />
            </Box>

            <TextField
              label="Username"
              name="username"
              fullWidth
              value={formData.username}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Password"
              name="password"
              fullWidth
              value={formData.password}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.active}
                  onChange={handleInputChange}
                  name="active"
                  color="primary"
                />
              }
              label="Active"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeModal} color="inherit">
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              {editingProxy ? "Update Proxy" : "Create Proxy"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Test Result Modal */}
      <Dialog
        open={!!testResult}
        onClose={() => setTestResult(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Proxy Test Result</DialogTitle>
        <DialogContent>
          {testResult && (
            <Box sx={{ mt: 2 }}>
              <Alert
                severity={testResult.success ? "success" : "error"}
                sx={{ mb: 2 }}
              >
                {testResult.message}
              </Alert>

              <Typography variant="subtitle1" fontWeight="bold">
                Detected IP:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 2,
                  fontFamily: "monospace",
                  bgcolor: "grey.100",
                  p: 1,
                  borderRadius: 1,
                }}
              >
                {testResult.proxy_ip || "N/A"}
              </Typography>

              <Typography variant="subtitle1" fontWeight="bold">
                Page Content Preview:
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: "grey.50",
                  maxHeight: 300,
                  overflow: "auto",
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontSize: "0.875rem",
                  }}
                >
                  {testResult.page_content_preview || "No content"}
                </pre>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTestResult(null)}
            color="primary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProxySubSubTab;
